<script>
    import {onMount} from 'svelte';

    import {mapStore} from '@js/stores/mapStore';
    import {userStore} from '@js/stores/userStore';

    import I18n from '@js/modules/translations';
    import StorageService from '@js/modules/storage';
    import AnalyticsService from '@js/modules/analyticsService';

    import {
        HomeOnboardingSteps,
        HomeOnboardingEnabled,
        HomeOnboardingStorageKey
    } from '@js/components/modules/constants';

    const HIGHLIGHT_TARGET = {
        type:     '#route-type-bar',
        data:     '#home-route-details',
        features: '#home-route-info',
        // Target the save button itself (not its full-width wrapper) so the highlight hugs it.
        save:     '#home-ride-creation .button-primary-contained'
    };

    const stored = StorageService.getItem(HomeOnboardingStorageKey) || {};

    let step = $state(stored.step || 'type');
    let dismissed = $state(!!stored.dismissed);
    let completed = $state(!!stored.completed);
    // Persisted so a page reload does not re-fire a step's "shown" event. Plain (non-$state)
    // on purpose: it's only read inside effects/handlers, never in the template — keeping it
    // reactive would make the "shown" effect re-run when it writes to itself.
    let shownSteps = Array.isArray(stored.shown) ? stored.shown : [];
    // How many times the tour has been re-shown after a map clear (capped).
    let reshowCount = stored.reshow || 0;

    // Connected users who already saved a route don't need the onboarding.
    const savedRide = !!window.userHasSavedRide;
    let highlightTimer;
    let _highlightedEl = null;

    const ONBOARDING_MAX_RESHOWS = 2;

    const _persist = () => StorageService.setItem(HomeOnboardingStorageKey, {step, dismissed, completed, shown: shownSteps, reshow: reshowCount});

    const eligible = $derived(
        HomeOnboardingEnabled && !dismissed && !completed && (!$userStore.isConnected || !savedRide)
    );

    const routeStarted = $derived(!!$mapStore.currentDistance);
    const hasStartPoint = $derived(!!$mapStore.startPointLocation);
    const mapReady = $derived(!!$mapStore.map);

    const _isPreDrawStep = (value) => value === 'type' || value === 'draw';
    const visibleStep = $derived(
        !routeStarted
            ? (step === 'type' ? 'type' : 'draw')
            : (_isPreDrawStep(step) ? 'data' : step)
    );

    $effect(() => {
        userStore.onboardingActive = eligible;

        if (!eligible) {
            _clearHighlight();
        }
    });

    $effect(() => {
        if (hasStartPoint && step === 'type') {
            step = 'draw';
            _persist();
        }
    });

    $effect(() => {
        if (routeStarted && _isPreDrawStep(step)) {
            step = 'data';
            _persist();
        }
    });

    const ONBOARDING_MAX_POINTS = 6;
    $effect(() => {
        if (!$mapStore.currentDistance || dismissed || completed) {
            return;
        }

        const linePoints = $mapStore.map?._mapModel?.linePoints || [];
        const waypointCount = linePoints.filter((point) => !point.computed).length;
        if (waypointCount >= ONBOARDING_MAX_POINTS) {
            dismissed = true;
            _persist();
        }
    });

    $effect(() => {
        if (!eligible) {
            return;
        }

        const current = visibleStep;
        if (!shownSteps.includes(current)) {
            shownSteps = [...shownSteps, current];
            _persist();
            AnalyticsService.trackOnboardingStep(current, 'shown');
        }

        if (mapReady) {
            _ring(current);
        }
    });

    const _target = (current) => {
        const selector = HIGHLIGHT_TARGET[current];
        return selector ? document.querySelector(selector) : null;
    };

    const _clearHighlight = () => {
        clearTimeout(highlightTimer);
        if (_highlightedEl) {
            _highlightedEl.classList.remove('onboarding-highlight');
            _highlightedEl = null;
        }
    };

    const _ring = (current) => {
        const wrapper = _target(current);
        const element = (current === 'type' || current === 'save') ? wrapper : (wrapper?.firstElementChild || wrapper);
        if (!element) {
            return;
        }

        _clearHighlight();
        _highlightedEl = element;
        element.classList.add('onboarding-highlight');
        highlightTimer = setTimeout(_clearHighlight, 2600);
    };

    // Explicit user action: bring the section to the centre (above the bottom card) + ring.
    const _scrollTo = (current) => {
        const element = _target(current);
        if (!element) {
            return;
        }

        element.scrollIntoView({behavior: 'smooth', block: 'center'});
        _ring(current);
    };

    const _next = () => {
        AnalyticsService.trackOnboardingStep(visibleStep, 'next');

        const index = HomeOnboardingSteps.indexOf(visibleStep);
        step = HomeOnboardingSteps[Math.min(index + 1, HomeOnboardingSteps.length - 1)];
        _persist();
        _scrollTo(step);
    };

    const _goTo = (target) => {
        if (target === visibleStep) {
            return;
        }

        if (!routeStarted && target !== 'type' && target !== 'draw') {
            return;
        }

        AnalyticsService.trackOnboardingStep(visibleStep, 'next');
        step = target;
        _persist();
        _scrollTo(target);
    };

    const _dismiss = () => {
        AnalyticsService.trackOnboardingStep(visibleStep, 'dismissed');
        dismissed = true;
        _persist();
    };

    const _complete = () => {
        if (completed) {
            return;
        }

        AnalyticsService.trackOnboardingStep('save', 'completed');
        completed = true;
        _persist();
    };

    const _handleMapCleared = () => {
        if (reshowCount >= ONBOARDING_MAX_RESHOWS) {
            return;
        }

        reshowCount += 1;
        step = 'type';
        dismissed = false;
        completed = false;
        shownSteps = [];
        _persist();
    };

    onMount(() => {
        const onRideSaved = () => _complete();
        window.addEventListener('mapplanner:ride-saved', onRideSaved);

        const onMapCleared = () => _handleMapCleared();
        window.addEventListener('mapplanner:map-cleared', onMapCleared);

        return () => {
            window.removeEventListener('mapplanner:ride-saved', onRideSaved);
            window.removeEventListener('mapplanner:map-cleared', onMapCleared);
            _clearHighlight();
            userStore.onboardingActive = false;
        };
    });
</script>

{#if eligible}
    <div class="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:max-w-sm z-[1050] rounded-xl border border-primary-200 bg-white shadow-lg p-4 dark:bg-gray-800 dark:border-gray-700">
        <div class="flex items-start justify-between gap-3">
            <div>
                <p class="text-sm font-semibold text-primary-800 dark:text-primary-300">
                    {I18n.t(`js.onboarding.home.${visibleStep}.title`)}
                </p>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {I18n.t(`js.onboarding.home.${visibleStep}.text`)}
                </p>
            </div>
            <button type="button"
                    aria-label="dismiss"
                    class="shrink-0 text-lg leading-none text-gray-400 hover:text-gray-600"
                    onclick={_dismiss}>
                &times;
            </button>
        </div>

        <div class="mt-3 flex items-center justify-between">
            <div class="flex gap-1">
                {#each HomeOnboardingSteps as onboardingStep (onboardingStep)}
                    <button type="button"
                            aria-label={onboardingStep}
                            class="h-1.5 w-4 rounded-full cursor-pointer transition-colors"
                            class:bg-primary-500={HomeOnboardingSteps.indexOf(visibleStep) >= HomeOnboardingSteps.indexOf(onboardingStep)}
                            class:bg-gray-200={HomeOnboardingSteps.indexOf(visibleStep) < HomeOnboardingSteps.indexOf(onboardingStep)}
                            onclick={() => _goTo(onboardingStep)}></button>
                {/each}
            </div>

            <div class="flex items-center gap-2">
                {#if visibleStep === 'save'}
                    <button type="button"
                            class="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                            onclick={_complete}>
                        {I18n.t('js.onboarding.home.done')}
                    </button>
                {:else if visibleStep === 'data' || visibleStep === 'features'}
                    <button type="button"
                            class="rounded-lg px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-gray-700"
                            onclick={() => _scrollTo(visibleStep)}>
                        {I18n.t('js.onboarding.home.see')}
                    </button>
                    <button type="button"
                            class="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                            onclick={_next}>
                        {I18n.t('js.onboarding.home.next')}
                    </button>
                {:else if visibleStep === 'type'}
                    <button type="button"
                            class="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                            onclick={_next}>
                        {I18n.t('js.onboarding.home.next')}
                    </button>
                {/if}
            </div>
        </div>
    </div>
{/if}

<style>
    :global(.onboarding-highlight) {
        outline: 2px solid #f59e0b;
        outline-offset: 2px;
        border-radius: 10px;
        box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.12);
        transition: outline-color 0.2s ease, box-shadow 0.2s ease;
    }
</style>
